import React, { useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './BaseModal.css';

/**
 * Base Modal Component
 * Provides common modal functionality with customizable content
 */
const BaseModal = ({
  children,
  onClose,
  title,
  size = 'medium',
  type = 'default',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className = '',
  ...props
}) => {
  const modalContentRef = useRef(null);
  const clickInsideRef = useRef(false);

  // Handle escape key
  useEffect(() => {
    if (!closeOnEscape) return;

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose, closeOnEscape]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []);

  const handleMouseDown = (event) => {
    if (
      modalContentRef.current &&
      modalContentRef.current.contains(event.target)
    ) {
      clickInsideRef.current = true; // Click started inside
    } else {
      clickInsideRef.current = false; // Click started outside
    }
  };

  const handleMouseUp = (event) => {
    if (!clickInsideRef.current && closeOnOverlayClick) {
      onClose(); // Close modal only if the click started outside
    }
  };

  const handleCloseClick = (event) => {
    event.stopPropagation(); // Prevent event from bubbling up
    onClose();
  };

  const getModalClasses = () => {
    const baseClass = 'eng-modal';
    const sizeClass = size !== 'medium' ? `eng-modal-${size}` : '';
    const typeClass = type !== 'default' ? `eng-modal-${type}` : '';
    
    return [baseClass, sizeClass, typeClass, className]
      .filter(Boolean)
      .join(' ');
  };

  return (
    <div
      className="eng-modal-overlay"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      {...props}
    >
      <div className={getModalClasses()} ref={modalContentRef}>
        {showCloseButton && (
          <button className="eng-modal-close" onClick={handleCloseClick}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
        
        {title && (
          <div className="eng-modal-header">
            <h3 className="eng-modal-title">{title}</h3>
          </div>
        )}
        
        <div className="eng-modal-content">
          {children}
        </div>
      </div>
    </div>
  );
};

// Convenience components for common modal types
export const FormModal = (props) => (
  <BaseModal type="form" size="medium" {...props} />
);

export const TableModal = (props) => (
  <BaseModal type="table" size="extra-large" {...props} />
);

export const ConfirmationModal = (props) => (
  <BaseModal type="confirmation" size="small" {...props} />
);

export default BaseModal;
